/**
 * The voice-channel cues — Discord's vocabulary: a friendly rising sound when
 * someone joins and its falling twin when they leave (recordings shared with
 * the notification sounds, lib/sounds.ts), a short upward sweep when a screen
 * goes up and a downward one when it comes down (synthesised here). You hear
 * them for yourself as well as for the others, because the sound is what tells
 * you the click actually did something.
 *
 * The two share sweeps are synthesised with the Web Audio API: tiny envelopes,
 * a few lines of code, they load instantly and never 404. Everything
 * is best-effort — audio must never break a call, so every failure is
 * swallowed. The context is created lazily on the first cue, which always
 * follows the user's own "join" click, so autoplay policy lets it run.
 */

import { playCue } from './sounds';

let context: AudioContext | null = null;

function audio(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!context) context = new Ctor();
  // Browsers suspend a context created outside a gesture; resuming is a no-op
  // when it is already running and otherwise takes effect for the next cue.
  if (context.state === 'suspended') void context.resume().catch(() => undefined);
  return context;
}

interface Note {
  /** Start frequency in Hz; the tone glides to `to` when it is given. */
  from: number;
  to?: number;
  /** Offset from the cue's start, seconds. */
  at: number;
  /** Seconds. */
  duration: number;
}

/** Quiet enough to sit under a conversation, loud enough to be noticed. */
const GAIN = 0.12;

function play(notes: Note[], type: OscillatorType = 'sine') {
  try {
    const ctx = audio();
    if (!ctx) return;
    const now = ctx.currentTime;
    for (const note of notes) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      const start = now + note.at;
      const end = start + note.duration;
      osc.frequency.setValueAtTime(note.from, start);
      if (note.to !== undefined) osc.frequency.exponentialRampToValueAtTime(note.to, end);
      // A short attack and a longer release, so the tone rings rather than clicks.
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(GAIN, start + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, end);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(end + 0.02);
    }
  } catch {
    // No audio device, a closed context, a policy refusal — the call goes on.
  }
}

/** Someone (you included) walked into the channel (lib/sounds.ts `join`). */
export function playJoinCue() {
  playCue('join');
}

/** Someone left (lib/sounds.ts `leave`). */
export function playLeaveCue() {
  playCue('leave');
}

/** A screen went up: a quick upward sweep. */
export function playShareStartCue() {
  play([{ from: 440, to: 880, at: 0, duration: 0.18 }], 'triangle');
}

/** A screen came down: the sweep in reverse. */
export function playShareStopCue() {
  play([{ from: 880, to: 440, at: 0, duration: 0.18 }], 'triangle');
}
